// "By feature flag" (D62 "crashes by feature flag"; plan step 3.6): for each flag evaluated in
// an issue's sessions, its variants with the share of their sessions that hit the issue. A bar per
// variant on that share, never a hue per variant (docs/brand.md, Charts). Each variant opens the
// issue's sessions with that variant. Flag names and variants are the app's: never translated.
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { issueFlagsQuery } from '../api/queries'
import { useI18n } from '../i18n'
import { Section } from './d'
import { ErrorState, Loading } from './states'

/** Flags shown, the one with the most occurrences first. */
const FLAGS_SHOWN = 4

export function FlagsPanel({
  orgId,
  projectId,
  issueId,
  appId,
}: {
  orgId: string
  projectId: string
  issueId: string
  appId: string
}) {
  const { t, percent } = useI18n()
  const q = useQuery(issueFlagsQuery(projectId, issueId, appId))
  const flags = q.data?.flags.slice(0, FLAGS_SHOWN) ?? []
  return (
    <Section title={t('flags.issue.title')} meta={t('flags.issue.sub')} minor>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} retry={() => q.refetch()} />
      ) : flags.length === 0 ? (
        <p className="empty">{t('flags.issue.none')}</p>
      ) : (
        <div>
          {flags.flatMap((f) =>
            f.variants.map((v) => (
              <div className="share" key={`${f.name}=${v.variant}`}>
                <span>
                  <Link
                    to="/o/$orgId/p/$projectId/sessions"
                    params={{ orgId, projectId }}
                    search={{
                      app: appId,
                      issue: issueId,
                      range: '30d',
                      flag: f.name,
                      variant: v.variant,
                    }}
                    className="ul"
                  >
                    <span translate="no">{`${f.name} = ${v.variant || '∅'}`}</span>
                  </Link>
                  <span className="is-vs">
                    {t('flags.issue.share', { count: v.issue_sessions, total: v.sessions })}
                    {' · '}
                    {t('flags.issue.rate', {
                      rate: percent(v.rate ?? 0, 1),
                      occurrences: v.occurrences,
                    })}
                  </span>
                </span>
                <span className="b" aria-hidden="true">
                  <i style={{ width: `${Math.max(0, Math.min(100, (v.rate ?? 0) * 100))}%` }} />
                </span>
                <span>{percent(v.rate ?? 0, 1)}</span>
              </div>
            )),
          )}
        </div>
      )}
    </Section>
  )
}
